import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent, type ReactNode } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import { motion, MotionConfig } from "framer-motion";
import {
  ArrowLeft, ArrowRight, CalendarDays, Check, CircleCheck, Clock, Copy, Download, FastForward, Gauge, ListVideo, Maximize, MessageCircleQuestion,
  Pause, Pencil, Pin, Play, Radio, Rewind, Volume2, VolumeX, Youtube,
} from "lucide-react";
import { YOUTUBE_LIVE_URL } from "@/lib/schedule";
import { EASE, HEART, Head, Ink, VIEW, datePart } from "../bits";
import { Part } from "../Page";
import { Locked, LockedRow, M, MembersPage, ReviewControls, lockReason, useRole, useTo } from "./Members";
import { CHELSEA, ME, MEMBERS, byNewest, can, classStart, classStatus, type ClassVideo, type Member, type Project } from "./sample";
import {
  ClassDialog, Progress, Skill, airDay, airLine, clock, human, isDone, isOut, library, partOf, projectById, projectClasses, seenBy, toSec, useLibrary, useNow, useToast,
} from "./Classes";
import "./Klass.css";

/* One club class (/blue-2/members/classes/:id): the replay with a chapter list
   that seeks it, mark-as-watched, the project's supplies (a checklist), the
   pattern, the other classes in the project, and a questions box.

   A class that hasn't aired yet (tonight's, say: see hasReplay in sample.ts)
   shows an "airs tonight" card instead of the player, the
   chapter plan instead of seekable chapters, and an "ask before class" box.
   The review bar can flip such a class to "on air" to check that state.

   The replay is a stand-in: every class plays /video/live-class.mp4, a 44-second
   clip, on loop. The player keeps its own class clock (1:32:10 long, say) so
   chapters, the scrubber and progress behave like the real thing; the clip
   just loops underneath. Swap in the real stream when there's a backend. */

const STANDIN = { src: "/video/live-class.mp4", poster: "/video/live-class.jpg" };
const RATES = [1, 1.25, 1.5, 2];
/** Where a class is for this page: a watchable replay, coming up, or on air right now. */
type Stage = "replay" | "upcoming" | "live";

/* ─── Sample questions (prototype only; would come from the backend) ─── */

type Reply = { id: string; by: Member; text: string; ago: string };
type Question = { id: string; by: Member; text: string; ago: string; same: number; pinned?: boolean; replies: Reply[] };
const [sam, jo, lee, mara, dana, priya] = MEMBERS;
const QUESTIONS: Record<string, Question[]> = {
  // The tote classes haven't aired yet, so these are questions asked ahead of class.
  "tote-1": [
    { id: "q1", by: priya, text: "Should I pre-wash my fat quarters before we cut tonight?", ago: "1d", same: 6, pinned: true,
      replies: [{ id: "q1r", by: CHELSEA, text: "Only the reds and darks! Everything else can go straight to the cutting mat. I'll talk about it at the start.", ago: "20h" }] },
    { id: "q2", by: jo, text: "Can I use a 5\" charm pack instead of cutting my own squares?", ago: "10h", same: 3,
      replies: [{ id: "q2r", by: CHELSEA, text: "Yes! Bring it tonight and I'll show you the layout.", ago: "9h" }, { id: "q2r2", by: mara, text: "Same plan here, see you there!", ago: "6h" }] },
    { id: "q3", by: lee, text: "Can I skip the fusible fleece if my main fabric is canvas?", ago: "3h", same: 1, replies: [] },
  ],
  "tote-2": [
    { id: "q4", by: dana, text: "How long will the straps be? I want mine to fit over a winter coat.", ago: "1d", same: 4,
      replies: [{ id: "q4r", by: CHELSEA, text: "26\" finished. Cut them 2–3\" longer for coat season, and I'll show both on Thursday.", ago: "1d" }] },
  ],
  "vest-1": [
    { id: "q5", by: sam, text: "My quilt has a lot of hand quilting. Should I stay-stitch before cutting?", ago: "1mo", same: 9,
      replies: [{ id: "q5r", by: CHELSEA, text: "Yes! Stay-stitch 1/8\" inside every cut line first. It keeps the quilting from unravelling.", ago: "1mo" }] },
  ],
  "vest-2": [{ id: "q6", by: mara, text: "Can I use snaps instead of buttons? I'm scared of buttonholes.", ago: "1mo", same: 5,
    replies: [{ id: "q6r", by: CHELSEA, text: "Snaps are perfect. Skip the buttonhole part at 1:10:00 and follow the snap package.", ago: "1mo" }] }],
  "pouch-1": [{ id: "q7", by: jo, text: "My zipper pull keeps getting in the way of the foot. Tips?", ago: "2mo", same: 11,
    replies: [{ id: "q7r", by: CHELSEA, text: "Stop with the needle down, lift the foot, slide the pull behind you. Around 24:00!", ago: "2mo" }] }],
};

/* ─── Player ─── */

type Player = ReturnType<typeof usePlayer>;
/** How the replay was opened: fresh, picking up a member's progress, or at a linked time (?t=). */
type StartKind = "fresh" | "resume" | "link";

function usePlayer(id: string, total: number, start: number, startKind: StartKind) {
  const video = useRef<HTMLVideoElement>(null);
  const shell = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState(start);
  const [playing, setPlaying] = useState(false);
  const [started, setStarted] = useState(false);
  const [muted, setMuted] = useState(true);
  const [rate, setRate] = useState(1);

  // A new class (prev/next) starts over at its own start point.
  useEffect(() => { setPos(start); setPlaying(false); setStarted(false); }, [id, start]);

  // The class clock: ticks in real time (at the chosen speed) while playing.
  useEffect(() => {
    if (!playing) return;
    const t = window.setInterval(() => setPos(p => Math.min(total, p + 0.25 * rate)), 250);
    return () => window.clearInterval(t);
  }, [playing, rate, total]);
  useEffect(() => { if (pos >= total) setPlaying(false); }, [pos, total]);

  // Keep the stand-in clip in step with the clock.
  useEffect(() => {
    const v = video.current;
    if (!v) return;
    if (playing) v.play().catch(() => {}); else v.pause(); // the class clock runs even if the clip can't play
  }, [playing]);
  useEffect(() => { if (video.current) video.current.playbackRate = rate; }, [rate]);
  useEffect(() => { if (video.current) video.current.muted = muted; }, [muted]);

  const sync = (t: number) => { const v = video.current; if (v && v.duration) v.currentTime = t % v.duration; };
  const seek = (t: number, play = true) => {
    const next = Math.max(0, Math.min(total - 1, t));
    setPos(next); sync(next); setStarted(true);
    if (play) setPlaying(true);
  };
  const toggle = () => {
    if (!playing && pos >= total - 1) { seek(0); return; }
    setStarted(true); setPlaying(p => !p);
  };
  return { video, shell, pos, total, playing, started, muted, rate, startKind, seek, toggle, sync, setMuted, setRate };
}

const activeIndex = (c: ClassVideo, pos: number) => c.chapters.reduce((a, ch, i) => (toSec(ch.t) <= pos + 0.5 ? i : a), 0);

function PlayerView({ c, pl }: { c: ClassVideo; pl: Player }) {
  const { pos, total, playing, started, muted, rate } = pl;
  const chapter = c.chapters[activeIndex(c, pos)];
  const resumeAt = !started && pos > 1;
  const onKey = (e: KeyboardEvent) => {
    const tag = (e.target as HTMLElement).tagName;
    if (tag === "INPUT" || e.metaKey || e.ctrlKey || e.altKey) return;
    if (e.key === " " && tag === "BUTTON") return; // let Space press the focused control
    if (e.key === " " || e.key === "k") { e.preventDefault(); pl.toggle(); }
    else if (e.key === "ArrowRight") { e.preventDefault(); pl.seek(pos + 15, playing); }
    else if (e.key === "ArrowLeft") { e.preventDefault(); pl.seek(pos - 15, playing); }
    else if (e.key === "m") pl.setMuted(m => !m);
  };
  const full = () => { const el = pl.shell.current; if (!el) return; if (document.fullscreenElement) document.exitFullscreen(); else el.requestFullscreen?.().catch(() => {}); };
  return <div ref={pl.shell} className={`mk-player${playing ? " is-playing" : ""}${started ? " is-started" : ""}`} tabIndex={-1} onKeyDown={onKey}
    role="region" aria-label={`Class replay: ${c.title}`}>
    <video ref={pl.video} className="mk-video" src={STANDIN.src} poster={c.poster || STANDIN.poster} loop playsInline muted preload="metadata"
      onLoadedMetadata={() => pl.sync(pos)} onClick={pl.toggle} aria-hidden="true" />
    {!started && <button type="button" className="mk-big" onClick={() => (resumeAt ? pl.seek(pos) : pl.toggle())}>
      <b><Play size={30} fill="currentColor" aria-hidden="true" /></b>
      <span>{resumeAt ? `${pl.startKind === "link" ? "Play from" : "Resume at"} ${clock(pos)}` : "Play class"}</span>
      {resumeAt && pl.startKind === "link" && chapter && <small>{chapter.label}</small>}
    </button>}
    <div className="mk-bar">
      <div className="mk-scrub">
        <span className="mk-scrub-fill" style={{ width: `${(pos / total) * 100}%` }} aria-hidden="true" />
        {c.chapters.slice(1).map(ch => <i key={ch.t} className="mk-tick" style={{ left: `${(toSec(ch.t) / total) * 100}%` }} aria-hidden="true" />)}
        <input type="range" min={0} max={total} step={1} value={Math.floor(pos)} onChange={e => pl.seek(Number(e.target.value), playing)}
          aria-label="Seek" aria-valuetext={`${clock(pos)} of ${clock(total)}, ${chapter?.label ?? ""}`} />
      </div>
      <div className="mk-ctrls">
        <button type="button" onClick={pl.toggle} aria-label={playing ? "Pause" : "Play"}>{playing ? <Pause size={20} fill="currentColor" aria-hidden="true" /> : <Play size={20} fill="currentColor" aria-hidden="true" />}</button>
        <button type="button" onClick={() => pl.seek(pos - 15, playing)} aria-label="Back 15 seconds"><Rewind size={18} aria-hidden="true" /></button>
        <button type="button" onClick={() => pl.seek(pos + 15, playing)} aria-label="Forward 15 seconds"><FastForward size={18} aria-hidden="true" /></button>
        <span className="mk-time"><b>{clock(pos)}</b> / {clock(total)}</span>
        <span className="mk-now" aria-hidden="true">{chapter?.label}</span>
        <button type="button" className="mk-rate" onClick={() => pl.setRate(r => RATES[(RATES.indexOf(r) + 1) % RATES.length])} aria-label={`Playback speed ${rate}×`}>
          <Gauge size={16} aria-hidden="true" />{rate}×</button>
        <button type="button" onClick={() => pl.setMuted(m => !m)} aria-label={muted ? "Unmute" : "Mute"} aria-pressed={!muted}>{muted ? <VolumeX size={19} aria-hidden="true" /> : <Volume2 size={19} aria-hidden="true" />}</button>
        <button type="button" onClick={full} aria-label="Full screen"><Maximize size={18} aria-hidden="true" /></button>
      </div>
    </div>
  </div>;
}

/** The replay's spot before it exists: when the class airs, a countdown, and the way into the live room. */
function Soon({ c, stage }: { c: ClassVideo; stage: Exclude<Stage, "replay"> }) {
  const role = useRole();
  const to = useTo();
  const now = useNow();
  const start = classStart(c), live = stage === "live", day = airDay(c, now);
  const wait = Math.max(60, (start.getTime() - now.getTime()) / 1000);
  const days = Math.round(wait / 86400);
  return <div className={`mk-player mk-soon${live ? " is-live" : ""}`}>
    <img src={c.poster || STANDIN.poster} alt="" />
    <div className="mk-soon-card">
      <span className="mk-soon-cal" aria-hidden="true">
        <small>{datePart(start, { weekday: "short" })}</small>
        <b>{datePart(start, { day: "numeric" })}</b>
        <span>{datePart(start, { month: "short" })}</span>
      </span>
      <div className="mk-soon-copy">
        <p className="mk-soon-hand">{live ? "we’re live right now!" : day === "tonight" ? "airs tonight!" : `airs ${day}`}</p>
        <p className="mk-soon-title">{live ? "On air now, come sew along" : `Live ${day === "tonight" || day === "tomorrow" ? day : `on ${day}`} at 7 PM MT`}</p>
        <p className="mk-soon-text">{live
          ? "Chelsea’s answering questions as you go. The replay, with chapters, lands here within the hour after class."
          : <>{days >= 2 ? `In ${days} days. ` : `Starts in ${human(wait)}. `}Sew along live in the club room. The replay lands here the same night, chapters and all.</>}</p>
        <div className="mk-soon-actions">
          <Link to={to(M.live)} className="mb-btn mb-btn-ink">{live ? <><Radio size={16} aria-hidden="true" /> {role === "chelsea" ? "Open the live room" : "Join the live class"}</> : <>Go to the live room <ArrowRight size={16} aria-hidden="true" /></>}</Link>
          <a href="#mk-sup-title" className="mk-soon-link">Check your supplies</a>
        </div>
      </div>
    </div>
  </div>;
}

function Chapters({ c, pl, open, out }: { c: ClassVideo; pl: Player; open: boolean; out: boolean }) {
  const list = useRef<HTMLOListElement>(null);
  const active = activeIndex(c, pl.pos);
  const total = pl.total;
  const seekable = open && out;
  // Keep the playing chapter in view inside the panel (never scrolls the page).
  useEffect(() => {
    const ol = list.current, li = ol?.children[active] as HTMLElement | undefined;
    if (!ol || !li || ol.scrollHeight <= ol.clientHeight) return;
    if (li.offsetTop < ol.scrollTop || li.offsetTop + li.offsetHeight > ol.scrollTop + ol.clientHeight) ol.scrollTo({ top: li.offsetTop - 12, behavior: "smooth" });
  }, [active]);
  return <section className="mk-chapters" aria-labelledby="mk-ch-title">
    <h2 id="mk-ch-title" className="mk-chapters-title"><ListVideo size={18} aria-hidden="true" /> {!out ? "The plan for this class" : open ? "Chapters" : "What’s in this class"} <span>{c.chapters.length}</span></h2>
    <ol ref={list}>
      {c.chapters.map((ch, i) => {
        const s = toSec(ch.t), end = i + 1 < c.chapters.length ? toSec(c.chapters[i + 1].t) : total;
        const on = seekable && pl.started && i === active;
        const inner = <><span className="mk-ch-t">{out ? ch.t : i + 1}</span><span className="mk-ch-l">{ch.label}</span>{out && <span className="mk-ch-d">{human(end - s)}</span>}</>;
        return <li key={`${ch.t}-${i}`}>
          {seekable
            ? <button type="button" className={`mk-ch${on ? " is-on" : ""}`} aria-current={on ? "true" : undefined} onClick={() => pl.seek(s)}
              aria-label={`${ch.label}, starts at ${ch.t}`}>{inner}{on && <span className="mk-ch-eq" aria-hidden="true"><i /><i /><i /></span>}</button>
            : <span className={`mk-ch is-static${out ? "" : " is-plan"}`}>{inner}</span>}
        </li>;
      })}
      <li className="mk-ch-end" aria-hidden="true">
        <svg viewBox="0 0 120 24"><Ink d="M4 14 C 18 4, 26 22, 40 12 S 62 4, 74 14 S 98 20, 112 8" width={2.5} /></svg>
        <span>{!out ? "chapters land with the replay ♥" : open ? "…and that’s the whole class ♥" : "all of this, for members ♥"}</span>
      </li>
    </ol>
    {seekable && <p className="mk-chapters-foot">Tap a chapter to jump there. Keys: <kbd>K</kbd> play/pause, <kbd>←</kbd> <kbd>→</kbd> 15 seconds.</p>}
  </section>;
}

/* ─── Header (teal): title, player, chapters ─── */

function Header({ c, p, pl, stage, onEdit }: { c: ClassVideo; p?: Project; pl: Player; stage: Stage; onEdit: () => void }) {
  const role = useRole();
  const to = useTo();
  const now = useNow();
  const { classes, watched, fresh } = useLibrary();
  const open = can(role, "classes"), out = stage === "replay";
  const w = out ? watched[c.id] ?? 0 : 0, done = isDone(w);
  const siblings = projectClasses(classes, c.projectId);
  const next = siblings[siblings.findIndex(x => x.id === c.id) + 1];
  const left = pl.total - Math.max(pl.pos, w * pl.total);
  // Only a member's own viewing of a real replay counts toward their progress (not Chelsea previewing).
  useEffect(() => { if (out && role === "member" && pl.playing) library.progress(c.id, pl.pos / pl.total); }, [out, role, pl.playing, pl.pos, pl.total, c.id]);
  const nQs = QUESTIONS[c.id]?.length ?? 0;
  const when = airLine(c, now, stage === "live");
  return <div className="mk-head">
    <Link to={to(M.classes)} className="mk-back"><ArrowLeft size={15} aria-hidden="true" /> Class library</Link>
    <div className="mk-titlebar">
      <div>
        <motion.p className="mk-kicker" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>{p ? `${p.month} · ${p.title}` : "Club class"}</motion.p>
        <h1 className="mk-title">
          <motion.span className="mk-title-hand" initial={{ opacity: 0, rotate: -12 }} animate={{ opacity: 1, rotate: -4 }} transition={{ delay: 0.3, duration: 0.5, ease: EASE }}>{partOf(classes, c).toLowerCase()}:</motion.span>
          <motion.span className="mk-title-word" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.6, ease: EASE }}>{c.title}</motion.span>
        </h1>
        <ul className="mk-facts">
          {out ? <li><CalendarDays size={16} aria-hidden="true" /> Live {c.date}</li>
            : <li className="mk-fact-soon">{stage === "live" ? <Radio size={16} aria-hidden="true" /> : <CalendarDays size={16} aria-hidden="true" />} {when}</li>}
          <li><Clock size={16} aria-hidden="true" /> {out ? human(pl.total) : `About ${human(pl.total)}`}</li>
          {p && <li><Skill level={p.skill} /></li>}
        </ul>
      </div>
      {role === "chelsea" && <div className="mk-creator">
        <span className="mk-creator-stat">{out ? <><b>{seenBy(classes, c, fresh)}</b> members watched</> : when.split(" · ")[0]} · <b>{nQs}</b> {nQs === 1 ? "question" : "questions"}</span>
        <button type="button" className="mb-btn mb-btn-hero" onClick={onEdit}><Pencil size={16} aria-hidden="true" /> {out ? "Edit class & chapters" : "Edit class plan"}</button>
      </div>}
    </div>

    <motion.div className="mk-stage" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.6, ease: EASE }}>
      <div className="mk-stage-main">
        {!open
          ? <Locked what={out ? "replays" : "live"} image={c.poster || STANDIN.poster} className="mk-lock"
            secondary={{ label: "Browse the library", to: M.classes }}>
            {!out && <p className="mk-lock-when"><CalendarDays size={15} aria-hidden="true" /> {when}</p>}
          </Locked>
          : out ? <PlayerView c={c} pl={pl} /> : <Soon c={c} stage={stage} />}
        {open && <div className="mk-under">
          {!out
            ? <span className="mk-under-progress">{role === "chelsea" ? "After the live, upload the recording from the class library." : "Nothing to mark yet: the replay shows up here after class."}</span>
            : role === "member"
              ? <>
                <button type="button" className={`mb-btn mb-btn-sm ${done ? "mb-btn-hero" : "mb-btn-line"}`} aria-pressed={done}
                  onClick={() => library.setWatched(c.id, done ? Math.min(pl.pos / pl.total, 0.96) : 1)}>
                  {done ? <CircleCheck size={18} aria-hidden="true" /> : <Check size={18} aria-hidden="true" />}{done ? "Watched" : "Mark as watched"}
                </button>
                <span className="mk-under-progress" aria-live="polite">{done ? "Nice work! You’ve finished this one." : w > 0 || pl.started ? `${human(left)} left` : "Not started yet"}</span>
              </>
              : <span className="mk-under-progress">Creator view: members see their progress and a “Mark as watched” button here.</span>}
          {next && <Link to={to(M.klass(next.id))} className="mk-next">Next: {next.title} <ArrowRight size={15} aria-hidden="true" /></Link>}
        </div>}
      </div>
      <Chapters c={c} pl={pl} open={open} out={out} />
    </motion.div>
  </div>;
}

/* ─── Body: questions, project sidebar, the rest of the project ─── */

function Avatar({ m }: { m: Member }) {
  const n = m.id.charCodeAt(m.id.length - 1) % 4;
  return m.avatar ? <img className="mk-av" src={m.avatar} alt="" /> : <span className={`mk-av mk-av-${n}`} aria-hidden="true">{m.name[0]}</span>;
}

/** Comment text with timestamps ("58:02") turned into buttons that seek the replay. */
function Linked({ text, pl, open }: { text: string; pl: Player; open: boolean }) {
  const parts = text.split(/(\b\d{1,2}:\d{2}(?::\d{2})?\b)/g);
  return <>{parts.map((s, i) => (i % 2 && open && toSec(s) < pl.total
    ? <button key={i} type="button" className="mk-stamp" onClick={() => { pl.seek(toSec(s)); pl.shell.current?.scrollIntoView({ behavior: "smooth", block: "center" }); pl.shell.current?.focus({ preventScroll: true }); }}
      aria-label={`Jump to ${s} in the replay`}>{s}</button>
    : <span key={i}>{s}</span>))}</>;
}

function QuestionMeta({ q }: { q: Question | Reply }) {
  const answered = "replies" in q && q.replies.some(r => r.by.id === CHELSEA.id);
  return <p className="mk-q-by"><b>{q.by.name}</b>{q.by.badge === "chelsea" && <span className="mk-badge">Teacher</span>}<span>{q.ago}</span>
    {answered && <span className="mk-answered"><Check size={12} strokeWidth={3} aria-hidden="true" /> Answered</span>}</p>;
}

function Questions({ c, pl, stage }: { c: ClassVideo; pl: Player; stage: Stage }) {
  const role = useRole();
  const open = can(role, "classes"), chelsea = role === "chelsea", out = stage === "replay";
  const me: Member = chelsea ? CHELSEA : ME;
  const [qs, setQs] = useState<Question[]>(() => QUESTIONS[c.id] ?? []);
  const [text, setText] = useState("");
  const [stamp, setStamp] = useState(true);
  const [replying, setReplying] = useState<string | null>(null);
  const [reply, setReply] = useState("");
  const [same, setSame] = useState<Record<string, boolean>>({});
  useEffect(() => { setQs(QUESTIONS[c.id] ?? []); setReplying(null); }, [c.id]);
  const at = out ? Math.floor(pl.pos) : 0;
  const tonight = airDay(c) === "tonight";

  const ask = (e: FormEvent) => {
    e.preventDefault();
    if (!text.trim()) return;
    const body = stamp && at > 0 ? `At ${clock(at)}: ${text.trim()}` : text.trim();
    setQs(list => [{ id: `n${Date.now()}`, by: me, text: body, ago: "just now", same: 0, replies: [] }, ...list]);
    setText("");
  };
  const send = (e: FormEvent, qid: string) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setQs(list => list.map(q => (q.id === qid ? { ...q, replies: [...q.replies, { id: `r${Date.now()}`, by: me, text: reply.trim(), ago: "just now" }] } : q)));
    setReply(""); setReplying(null);
  };
  const sorted = [...qs].sort((a, b) => Number(!!b.pinned) - Number(!!a.pinned));
  const n = qs.length, nLabel = `${n} ${n === 1 ? "question" : "questions"}`;
  const placeholder = chelsea
    ? out ? "Post a note or tip for everyone watching this replay…" : "Post a note for everyone before class…"
    : out ? "What tripped you up? Chelsea answers questions most weekdays." : `Ask ahead and Chelsea will answer it live${tonight ? " tonight" : " in class"}.`;

  return <section className="mk-qs" aria-labelledby="mk-qs-title">
    {out ? <Head id="mk-qs-title" hand="stuck on a step?" word="ask away" tone="blue" className="mb-headsm" />
      : <Head id="mk-qs-title" hand="before class?" word="ask early" tone="blue" className="mb-headsm" />}
    {!open ? <Locked what="questions" className="mk-qs-lock"
      reason={<><b>{n ? `${nLabel} from members` : "Questions are for members"}</b>{n ? ", with Chelsea’s answers. " : ". "}{lockReason(role, "members", "questions")}</>}
      peek={n > 0 && <ul className="mk-qlist">{qs.slice(0, 2).map(q => <li key={q.id} className="mk-q">
        <div className="mk-q-main"><Avatar m={q.by} /><div><QuestionMeta q={q} /><p className="mk-q-text">{q.text}</p></div></div>
      </li>)}</ul>} />
      : <>
        <form className="mk-ask" onSubmit={ask}>
          <label htmlFor="mk-ask-text" className="mb-sr">Your question</label>
          <textarea id="mk-ask-text" value={text} onChange={e => setText(e.target.value)} rows={3} placeholder={placeholder} />
          <div className="mk-ask-row">
            {at > 0 && <label className="mc-check mk-ask-stamp"><input type="checkbox" checked={stamp} onChange={e => setStamp(e.target.checked)} /><span>Add the time I’m at ({clock(at)})</span></label>}
            <button type="submit" className="mb-btn mb-btn-ink" disabled={!text.trim()}><MessageCircleQuestion size={16} aria-hidden="true" /> {chelsea ? "Post" : out ? "Ask" : "Ask before class"}</button>
          </div>
        </form>
        <p className="mk-qs-count" aria-live="polite">{n ? (out ? nLabel : `${nLabel} so far`) : out ? "No questions yet. Be the first!" : "No questions yet. Ask one before class!"}</p>
        <ul className="mk-qlist">
          {sorted.map(q => <li key={q.id} className={`mk-q${q.pinned ? " is-pinned" : ""}`}>
            {q.pinned && <p className="mk-pinned"><Pin size={13} aria-hidden="true" /> Pinned by Chelsea</p>}
            <div className="mk-q-main">
              <Avatar m={q.by} />
              <div>
                <QuestionMeta q={q} />
                <p className="mk-q-text"><Linked text={q.text} pl={pl} open={open && out} /></p>
                <div className="mk-q-tools">
                  {!chelsea && q.by.id !== ME.id && <button type="button" aria-pressed={!!same[q.id]} onClick={() => setSame(s => ({ ...s, [q.id]: !s[q.id] }))}>
                    {same[q.id] ? "✓ " : ""}Same question · {q.same + (same[q.id] ? 1 : 0)}</button>}
                  <button type="button" onClick={() => { setReplying(r => (r === q.id ? null : q.id)); setReply(""); }} aria-expanded={replying === q.id}>Reply</button>
                  {chelsea && <button type="button" aria-pressed={!!q.pinned} onClick={() => setQs(list => list.map(x => ({ ...x, pinned: x.id === q.id ? !x.pinned : false })))}>
                    <Pin size={13} aria-hidden="true" /> {q.pinned ? "Unpin" : "Pin"}</button>}
                </div>
              </div>
            </div>
            {q.replies.length > 0 && <ul className="mk-replies">{q.replies.map(r => <li key={r.id}>
              <Avatar m={r.by} />
              <div>
                <QuestionMeta q={r} />
                <p className="mk-q-text"><Linked text={r.text} pl={pl} open={open && out} /></p>
              </div>
            </li>)}</ul>}
            {replying === q.id && <form className="mk-reply" onSubmit={e => send(e, q.id)}>
              <label htmlFor={`mk-r-${q.id}`} className="mb-sr">Reply to {q.by.name}</label>
              <input id={`mk-r-${q.id}`} value={reply} onChange={e => setReply(e.target.value)} placeholder={`Reply to ${q.by.name}…`} autoFocus />
              <button type="submit" className="mb-btn mb-btn-ink mb-btn-sm" disabled={!reply.trim()}>Send</button>
            </form>}
          </li>)}
        </ul>
      </>}
  </section>;
}

function InProject({ c }: { c: ClassVideo }) {
  const role = useRole();
  const to = useTo();
  const now = useNow();
  const { classes, watched, fresh } = useLibrary();
  const list = projectClasses(classes, c.projectId);
  const p = projectById(c.projectId);
  const me = list.findIndex(y => y.id === c.id);
  return <section className="mk-proj" aria-labelledby="mk-proj-title">
    <Head id="mk-proj-title" hand={`the whole ${p ? p.title.toLowerCase() : "project"}`} word="class by class" tone="blue" className="mb-headsm mk-proj-head" />
    <ol className="mk-parts">
      {list.map((x, i) => {
        const here = x.id === c.id, out = isOut(x, fresh, now), w = watched[x.id] ?? 0, tag = i < me ? "Previous" : "Up next";
        const inner = <>
          <span className="mk-part-img"><img src={x.poster} alt="" loading="lazy" /><b aria-hidden="true">{i + 1}</b></span>
          <span className="mk-part-copy">
            <span className="mc-part">{here ? "You’re here" : tag} · {partOf(classes, x)}</span>
            <span className="mk-part-title">{x.title}</span>
            {out ? <span className="mc-meta">{x.length} · {x.date}</span> : <span className="mc-meta mc-soon-line"><b>{airLine(x, now).split(" · ")[0]}</b> · {airLine(x, now).split(" · ")[1]}</span>}
            {role === "member" && out && <Progress w={w} />}
          </span>
        </>;
        return <li key={x.id}>{here ? <div className="mk-part is-here" aria-current="page">{inner}</div>
          : <Link to={to(M.klass(x.id))} className="mk-part">{inner}<ArrowRight className="mk-part-go" size={18} aria-hidden="true" /></Link>}</li>;
      })}
    </ol>
    {list.length === 1 && <p className="mk-solo">A one-class project: everything you need is right here.</p>}
  </section>;
}

function Sidebar({ c, p, toast }: { c: ClassVideo; p?: Project; toast: (m: ReactNode) => void }) {
  const role = useRole();
  const open = can(role, "classes");
  const { classes } = useLibrary();
  const [got, setGot] = useState<Record<number, boolean>>({});
  useEffect(() => { setGot({}); }, [c.projectId]);
  if (!p) return null;
  const n = p.supplies.filter((_, i) => got[i]).length;
  const copy = () => {
    navigator.clipboard?.writeText(`${p.title} supplies:\n${p.supplies.map(s => `• ${s}`).join("\n")}`).catch(() => {});
    toast("Supply list copied. Take it to the fabric shop!");
  };
  return <aside className="mk-side" aria-label="Project details">
    <motion.figure className="mk-snap" initial={{ opacity: 0, rotate: 7, y: 20 }} whileInView={{ opacity: 1, rotate: 2.5, y: 0 }} viewport={VIEW} transition={{ type: "spring", stiffness: 120, damping: 15 }}>
      <i className="b2-snap-tape b2-ktape-soft" aria-hidden="true" />
      <img src={p.image} alt={`The finished ${p.title}`} />
      <figcaption>{p.title.toLowerCase()} ♥</figcaption>
    </motion.figure>
    <div className="mk-card">
      <p className="mk-card-kicker">{p.month}</p>
      <h2 className="mk-card-title">{p.title}</h2>
      <p className="mk-card-text">{p.blurb}</p>
      <dl className="mk-dl"><div><dt>Skill level</dt><dd><Skill level={p.skill} /></dd></div><div><dt>Classes</dt><dd>{projectClasses(classes, p.id).length}</dd></div></dl>
    </div>

    <section className="mk-card mk-supplies" aria-labelledby="mk-sup-title">
      <div className="mk-card-row">
        <h2 id="mk-sup-title" className="mk-card-title">Supplies</h2>
        {open && <span className="mk-sup-count" aria-live="polite">{n} of {p.supplies.length} ready</span>}
      </div>
      {open && <span className="mk-sup-bar" aria-hidden="true"><i style={{ width: `${(n / p.supplies.length) * 100}%` }} /></span>}
      <ul className="mk-sup-list">
        {p.supplies.map((s, i) => <li key={s}>
          {open ? <label className={got[i] ? "is-got" : ""}>
            <input type="checkbox" checked={!!got[i]} onChange={e => setGot(g => ({ ...g, [i]: e.target.checked }))} />
            <span className="mk-box" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="M5 12.5 L10 17 L19 7" /></svg></span>
            <span>{s}</span>
          </label> : <span className="mk-sup-plain">{s}</span>}
        </li>)}
      </ul>
      {open && n === p.supplies.length && <p className="mk-sup-done">All set. Thread that needle!</p>}
      <button type="button" className="mk-textbtn" onClick={copy}><Copy size={14} aria-hidden="true" /> Copy list</button>
    </section>

    <section className="mk-card mk-pattern" aria-labelledby="mk-pat-title">
      <h2 id="mk-pat-title" className="mk-card-title">Pattern</h2>
      {p.pattern
        ? open
          ? <><p className="mk-card-text">{p.pattern}</p>
            <button type="button" className="mb-btn mb-btn-ink mb-btn-wide mk-dl-btn" onClick={() => toast(<>Prototype: <b>{p.title} pattern.pdf</b> would download now.</>)}><Download size={16} aria-hidden="true" /> Download pattern</button></>
          : <><p className="mk-card-text">{p.pattern.replace(/\s*\(.*\)$/, "")} (PDF)</p><LockedRow className="mk-pat-lock" /></>
        : <p className="mk-card-text">No printed pattern for this one. Every measurement is in the class, and in the chapter list.</p>}
    </section>

    <a className="mk-yt" href={YOUTUBE_LIVE_URL} target="_blank" rel="noreferrer">
      <Youtube size={20} aria-hidden="true" />
      <span><b>Free most Tuesdays</b> Live on YouTube, 7 PM MT</span>
      <ArrowRight size={16} aria-hidden="true" />
    </a>
  </aside>;
}

function Body({ c, p, pl, stage }: { c: ClassVideo; p?: Project; pl: Player; stage: Stage }) {
  const [toast, showToast] = useToast();
  return <Part p="making">
    <section className="mk-body" aria-label="About this class">
      <div className="b2-wrap mk-body-grid">
        <div className="mk-main">
          <InProject c={c} />
          <Questions c={c} pl={pl} stage={stage} />
        </div>
        <Sidebar c={c} p={p} toast={showToast} />
      </div>
      <svg className="mk-body-heart" viewBox="0 0 30 46" aria-hidden="true"><Ink d={HEART} delay={0.4} width={3} /></svg>
    </section>
    {toast}
  </Part>;
}

/* ─── Not found ─── */

function Missing() {
  const to = useTo();
  return <div className="mk-missing">
    <p className="mk-kicker">Class library</p>
    <h1 className="mk-title"><span className="mk-title-hand">hmm, that class</span><span className="mk-title-word">wandered off</span></h1>
    <p className="mk-missing-text">It may have been renamed or moved. Every replay is in the library.</p>
    <Link to={to(M.classes)} className="mb-btn mb-btn-hero">Back to the library <ArrowRight size={16} aria-hidden="true" /></Link>
  </div>;
}

/** The 404's body: the newest replays (aired ones only), so a broken link still lands somewhere useful. */
function Recent() {
  const to = useTo();
  const now = useNow();
  const { classes, fresh } = useLibrary();
  const list = classes.filter(x => isOut(x, fresh, now)).sort(byNewest).slice(0, 3);
  return <Part p="making">
    <section className="mk-body mk-recent" aria-labelledby="mk-recent-title">
      <div className="b2-wrap">
        <Head id="mk-recent-title" hand="maybe you meant" word="these" tone="blue" className="mb-headsm" />
        <ol className="mk-parts">
          {list.map(x => <li key={x.id}><Link to={to(M.klass(x.id))} className="mk-part">
            <span className="mk-part-img"><img src={x.poster} alt="" loading="lazy" /></span>
            <span className="mk-part-copy">
              <span className="mc-part">{projectById(x.projectId)?.title} · {partOf(classes, x)}</span>
              <span className="mk-part-title">{x.title}</span>
              <span className="mc-meta">{x.length} · {x.date}</span>
            </span>
            <ArrowRight className="mk-part-go" size={18} aria-hidden="true" />
          </Link></li>)}
        </ol>
      </div>
    </section>
  </Part>;
}

function EditDialog({ c, open, onClose }: { c: ClassVideo; open: boolean; onClose: () => void }) {
  const role = useRole();
  if (!open || role !== "chelsea") return null;
  return <Part p="club"><ClassDialog editing={c} onClose={onClose} onSaved={x => library.save(x, false)} /></Part>;
}

/** Review-bar switch for a class that hasn't aired: see it before class or while it's on air. */
function StageSwitch({ stage, setStage }: { stage: Stage; setStage: (s: Stage) => void }) {
  return <ReviewControls label="This class">
    <button type="button" aria-pressed={stage === "upcoming"} onClick={() => setStage("upcoming")}>Before</button>
    <button type="button" aria-pressed={stage === "live"} onClick={() => setStage("live")}>On air</button>
  </ReviewControls>;
}

export function MembersKlass() {
  const { id = "" } = useParams();
  const [params] = useSearchParams();
  const now = useNow();
  const { classes, watched, fresh } = useLibrary();
  const c = classes.find(x => x.id === id);
  const p = c ? projectById(c.projectId) : undefined;
  const total = c ? Math.max(60, toSec(c.length)) : 60;
  // Tonight's (or any future) class has no replay yet: hasReplay() via isOut(). The review bar can override "before" / "on air".
  const out = !!c && isOut(c, fresh, now);
  const [review, setReview] = useState<{ id: string; stage: Stage } | null>(null);
  const stage: Stage = out ? "replay" : review?.id === id ? review.stage : c && classStatus(c, now) === "live" ? "live" : "upcoming";
  const asked = params.get("t");
  const w = watched[id] ?? 0;
  // The page sits outside the role provider, so read the reviewer's role from the URL. Only a member has progress to resume.
  const member = (params.get("as") ?? "member") === "member";
  // ?t= (from search or a timestamp link) wins; otherwise a member picks up where they left off. Captured once per class.
  const [start, startKind] = useStartFor(id, !out ? [0, "fresh"] : asked !== null && Number(asked) > 0 ? [Math.min(Number(asked), total - 1), "link"]
    : member && w > 0 && !isDone(w) ? [Math.floor(w * total), "resume"] : [0, "fresh"]);
  const pl = usePlayer(id, total, start, startKind);
  const [editing, setEditing] = useState(false);

  if (!c) return <MotionConfig reducedMotion="user"><MembersPage page="klass" header={<Missing />}><Recent /></MembersPage></MotionConfig>;
  return <MotionConfig reducedMotion="user">
    <MembersPage page="klass" header={<Header c={c} p={p} pl={pl} stage={stage} onEdit={() => setEditing(true)} />}>
      <Body c={c} p={p} pl={pl} stage={stage} />
      <EditDialog c={c} open={editing} onClose={() => setEditing(false)} />
      {!out && <StageSwitch stage={stage} setStage={s => setReview({ id, stage: s })} />}
    </MembersPage>
  </MotionConfig>;
}

/** The start point for a class, fixed when the class opens (not re-read as progress grows). */
function useStartFor(id: string, value: [number, StartKind]) {
  const ref = useRef<{ id: string; v: [number, StartKind] } | null>(null);
  if (!ref.current || ref.current.id !== id) ref.current = { id, v: value };
  return ref.current.v;
}
